import { ChevronDown, Info, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/shared/Input/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import useModalStore from '@/store/useModalStore';
import { FunnelType, Upsell } from '@/@types/Upsell';
import { OffersService } from '@/services/offers.service';
import Divider from '@/components/shared/Divider/Divider';
import { ComboBox } from '@/components/shared/ComboBox/ComboBox';
import { ColorPickerCustom as ColorPicker } from '@/components/shared/ColorPicker/ColorPicker';
import { isAxiosError } from 'axios';
import { toast } from '@/hooks/use-toast';
import { normalizeUrlWhenPaste } from '@/utils/normalize';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpsellSchema } from '../schemas/formSchemas';
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface EditUpsellSheetProps {
  productId: string;
  upsell: Upsell | null;
  onUpdate: (data: any) => Promise<void>;
  isLoading: boolean;
}

interface UpsellFormData {
  name: string;
  funnel_type: FunnelType;
  page: string;
  offer_id: string;
  accepted_page?: string | null;
  recused_page?: string | null;
  up_sell_display_text: string;
  up_sell_button_color: string;
  up_sell_display_text_color: string;
  down_sell_display_text: string;
  down_sell_button_color: string;
  down_sell_display_text_color: string;
}

export function EditUpsellSheet({
  productId,
  upsell,
  onUpdate,
  isLoading,
}: EditUpsellSheetProps) {
  const { t } = useTranslation(['upsells', 'common']);
  const { closeModal } = useModalStore();
  const { editUpsellSchema } = useUpsellSchema();
  const [showButtonCustomization, setShowButtonCustomization] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<UpsellFormData>({
    resolver: zodResolver(editUpsellSchema),
    defaultValues: {
      name: upsell?.name || '',
      funnel_type: upsell?.funnel_type || FunnelType.UpSell,
      page: upsell?.page || '',
      offer_id: upsell?.offer.id || '',
      accepted_page: upsell?.accepted_page || '',
      recused_page: upsell?.recused_page || '',
      up_sell_display_text: upsell?.up_sell_display_text || t('form_placeholders.default_accept', 'Sim, quero essa oferta!'),
      up_sell_button_color: upsell?.up_sell_button_color || '#5CCE5E',
      up_sell_display_text_color: upsell?.up_sell_display_text_color || '#000000',
      down_sell_display_text: upsell?.down_sell_display_text || t('form_placeholders.default_decline', 'Não, obrigado'),
      down_sell_button_color: upsell?.down_sell_button_color || '#DC2626',
      down_sell_display_text_color: upsell?.down_sell_display_text_color || '#FFFFFF',
    },
  });

  const formValues = watch();

  const fetchOptions = async (query: string) => {
    return await OffersService.getOffersToCreation({
      productId,
      q: query,
    });
  };

  const handleClose = () => {
    reset();
    setShowButtonCustomization(false);
    closeModal('funnel-edit-upsell');
  };

  const onSubmit = async (data: any) => {
    try {
      const payload = {
        ...data,
        accepted_page: data.accepted_page || null,
        recused_page: data.recused_page || null,
      };

      await onUpdate(payload);
      handleClose();
    } catch (error) {
      if (isAxiosError(error)) {
        toast({
          description:
            t(error?.response?.data?.message) ||
            t('common:data_submission_error'),
          variant: 'error',
        });
      } else {
        toast({
          description: t('common:data_submission_error'),
          variant: 'error',
        });
      }
    }
  };

  useEffect(() => {
    if (watch('page') && watch('page').startsWith('https://')) {
      clearErrors('page');
    }
  }, [watch('page'), clearErrors]);

  useEffect(() => {
    if (
      watch('accepted_page') &&
      watch('accepted_page').startsWith('https://')
    ) {
      clearErrors('accepted_page');
    }
  }, [watch('accepted_page'), clearErrors]);

  useEffect(() => {
    if (watch('recused_page') && watch('recused_page').startsWith('https://')) {
      clearErrors('recused_page');
    }
  }, [watch('recused_page'), clearErrors]);

  return (
    <section className='flex flex-col h-full overflow-hidden'>
      <SheetHeader className='p-6'>
        <SheetTitle className='flex gap-2'>{t('edit_strategy')} </SheetTitle>
      </SheetHeader>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className='flex flex-col justify-between h-full overflow-hidden'
      >
        <section className='flex flex-col gap-4 px-6 overflow-y-auto flex-1 pb-4'>
          <div className='grid grid-cols-2 gap-4 max-md:grid-cols-1'>
            <div className='mt-[1.5px]'>
              <Input
                label={t('form.name')}
                {...register('name')}
                helperText={errors.name?.message}
              />
            </div>
            <div>
              <label className='text-sm mb-1 block text-white'>
                {t('form.type')}
              </label>
              <Select
                onValueChange={(value) =>
                  setValue('funnel_type', value as FunnelType)
                }
                defaultValue={upsell?.funnel_type}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FunnelType.UpSell}>
                    {t('form.type_upsell')}
                  </SelectItem>
                  <SelectItem value={FunnelType.DownSell}>
                    {t('form.type_downsell')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <ComboBox
              fetchOptions={fetchOptions}
              value={watch('offer_id')}
              onChange={(value) => {
                clearErrors('offer_id');
                setValue('offer_id', value);
              }}
              label={t('common:offer')}
              placeholder={t('common:select_offer')}
              helperText={errors.offer_id?.message}
              isEditing
            />
          </div>

          <div>
            <Input
              label={t('form.page')}
              {...register('page', {
                setValueAs: (value: string) => {
                  const cleaned = value?.replace(/^(https?:\/\/)+/, '');
                  return `https://${cleaned}`;
                },
              })}
              helperText={errors.page?.message}
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://';
                  setValue('page', 'https://');
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e);
                setValue('page', finalValue);
              }}
              onChange={(e) => {
                if (!e.target.value.startsWith('https://')) {
                  e.target.value = 'https://';
                  setValue('page', 'https://');
                } else {
                  setValue('page', e.target.value);
                }
              }}
            />
          </div>

          <Divider />

          <div className='space-y-4'>
            <h3 className='text-lg font-medium text-zinc-100'>
              {t('redirect_pages')}
            </h3>

            <Input
              label={t('form.accepted_page')}
              {...register('accepted_page')}
              helperText={errors.accepted_page?.message}
              placeholder='https://exemplo.com/proximo-upsell'
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://';
                  setValue('accepted_page', 'https://');
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e);
                setValue('accepted_page', finalValue);
              }}
            />

            <Input
              label={t('form.recused_page')}
              {...register('recused_page')}
              helperText={errors.recused_page?.message}
              placeholder='https://exemplo.com/downsell'
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://';
                  setValue('recused_page', 'https://');
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e);
                setValue('recused_page', finalValue);
              }}
            />

            <div className='bg-[#18181B] p-4 rounded-md flex items-center'>
              <div className='flex gap-3'>
                <Info size={20} className='text-white flex-none' />
                <span className='text-[#A1A1AA] text-sm leading-relaxed'>
                  {t('redirect_description')}
                </span>
              </div>
            </div>
          </div>

          <Divider />

          <div className='rounded-lg border border-[#27272a]'>
            <button
              type='button'
              onClick={() => {
                if (showButtonCustomization) setIsExpanded(false);
                setShowButtonCustomization((prev) => !prev);
              }}
              className='w-full flex items-center justify-between p-4 text-left hover:bg-[#27272a]/40 transition-colors'
            >
              <div>
                <p className='text-sm font-medium text-zinc-100'>
                  {t('button_customization', 'Personalização dos botões')}
                </p>
                <p className='text-[11px] text-zinc-500 mt-0.5'>
                  {t('button_customization_desc', 'Texto e cores dos botões de aceitar/recusar')}
                </p>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-zinc-500 transition-transform duration-200 flex-shrink-0 ${
                  showButtonCustomization ? 'rotate-180' : ''
                }`}
              />
            </button>

            <AnimatePresence>
              {showButtonCustomization && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ overflow: isExpanded ? 'visible' : 'hidden' }}
                  onAnimationComplete={(definition) => {
                    if (definition === 'animate') setIsExpanded(true);
                  }}
                >
                  <div className='px-4 pb-4 space-y-5 border-t border-[#27272a] pt-4'>
                    <div className='space-y-3'>
                      <div className='flex items-center gap-2'>
                        <span className='h-2 w-2 rounded-full bg-emerald-500' />
                        <p className='text-xs font-semibold text-emerald-400'>
                          {t('accept_button_label', 'Botão Aceitar')}
                        </p>
                      </div>
                      <Input
                        label={t('form.up_sell_display_text')}
                        {...register('up_sell_display_text')}
                        helperText={errors.up_sell_display_text?.message}
                        placeholder='Sim, quero essa oferta!'
                      />
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                        <ColorPicker
                          defaultColor={formValues.up_sell_button_color}
                          onValueChange={(value) =>
                            setValue('up_sell_button_color', value)
                          }
                          label={t('form.color_bg')}
                        />
                        <ColorPicker
                          defaultColor={formValues.up_sell_display_text_color}
                          onValueChange={(value) =>
                            setValue('up_sell_display_text_color', value)
                          }
                          label={t('form.color_text')}
                        />
                      </div>
                    </div>

                    <div className='space-y-3'>
                      <div className='flex items-center gap-2'>
                        <span className='h-2 w-2 rounded-full bg-rose-500' />
                        <p className='text-xs font-semibold text-rose-400'>
                          {t('decline_button_label', 'Botão Recusar')}
                        </p>
                      </div>
                      <Input
                        label={t('form.down_sell_display_text')}
                        {...register('down_sell_display_text')}
                        helperText={errors.down_sell_display_text?.message}
                        placeholder='Não, obrigado'
                      />
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                        <ColorPicker
                          defaultColor={formValues.down_sell_button_color}
                          onValueChange={(value) =>
                            setValue('down_sell_button_color', value)
                          }
                          label={t('form.color_bg')}
                        />
                        <ColorPicker
                          defaultColor={formValues.down_sell_display_text_color}
                          onValueChange={(value) =>
                            setValue('down_sell_display_text_color', value)
                          }
                          label={t('form.color_text')}
                        />
                      </div>
                    </div>

                    <div className='p-3 rounded-lg border border-[#27272a] bg-[#09090b] space-y-2'>
                      <p className='text-[10px] text-zinc-500 text-center mb-1'>Preview</p>
                      <button
                        type='button'
                        className='w-full py-2.5 rounded-md text-xs font-medium transition-colors'
                        style={{
                          backgroundColor: formValues.up_sell_button_color,
                          color: formValues.up_sell_display_text_color,
                        }}
                      >
                        {formValues.up_sell_display_text || 'Aceitar'}
                      </button>
                      <button
                        type='button'
                        className='w-full py-2.5 rounded-md text-xs font-medium transition-colors'
                        style={{
                          backgroundColor: formValues.down_sell_button_color,
                          color: formValues.down_sell_display_text_color,
                        }}
                      >
                        {formValues.down_sell_display_text || 'Recusar'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        <div className='flex border-t items-center justify-end gap-4 px-6 py-4 w-full max-sm:pb-12 flex-shrink-0'>
          <Button type='button' variant='ghost' onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button disabled={isLoading} type='submit'>
            {isLoading ? (
              <span className='flex items-center gap-2'>
                <Loader2 className='animate-spin' />
                {t('common:saving')}
              </span>
            ) : (
              t('save_changes')
            )}
          </Button>
        </div>
      </form>
    </section>
  );
}
